import { Form, Select, Input, FormInstance } from 'antd'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { GoogleSheetData } from '../../pages/settings/company/integrations/types'
import { ReportDataType } from '../../redux/context/reports/types'
import { useBackend } from '../../services/backend'
import CtxDimensionTreeSelect from '../../features/dimension/components/CtxDimensionTreeSelect'
import CtxScenarioLockSelect from '../../features/scenario/components/select/CtxScenarioLockSelect'

interface GoogleSheetFormProps {
  form: FormInstance
  type: 'budget' | 'external' | 'actuals'
}

const GoogleSheetForm: React.FC<GoogleSheetFormProps> = ({ form, type }: GoogleSheetFormProps) => {
  const { t } = useTranslation()
  const [sheetData, setSheetData] = useState<GoogleSheetData>()
  const [url, setUrl] = useState<string>()
  const [error, setError] = useState(false)
  const sheetDataRequest = useBackend(`/google/sheets/{companyId}/data`, process.env.REACT_APP_INTEGRATION_URL)

  const sheetDataHandler = () => {
    url &&
      sheetDataRequest
        .get({ body: { params: { url } } })
        .then((response: GoogleSheetData) => {
          setError(false)
          setSheetData(response)
        })
        .catch(() => {
          setError(true)
          setSheetData(undefined)
        })
  }

  useEffect(sheetDataHandler, [url])

  const renderInfo = () => {
    if (error) {
      return t('integrationsPage:google-sheet-error')
    }
    if (!sheetDataRequest.loading && sheetData) {
      return sheetData.properties.title
    }
    return undefined
  }

  return (
    <>
      <Form.Item
        name="url"
        preserve
        rules={[
          {
            required: true,
            message: t('global:required-field')
          }
        ]}
        style={{ marginTop: 10 }}
        label={t('integrationsPage:url')}
        help={renderInfo()}
      >
        <Input onChange={(e: any) => setUrl(e.target.value)} />
      </Form.Item>
      <Form.Item
        label={t('integrationsPage:sheet')}
        name="sheet"
        rules={[
          {
            required: true,
            message: t('global:required-field')
          }
        ]}
        hasFeedback={
          form.getFieldValue('sheet') &&
          sheetData &&
          !sheetData.sheets.map(sheetsData => sheetsData.properties.title).includes(form.getFieldValue('sheet'))
        }
        validateStatus={
          form.getFieldValue('sheet') &&
          sheetData &&
          !sheetData.sheets.map(sheetsData => sheetsData.properties.title).includes(form.getFieldValue('sheet'))
            ? 'warning'
            : undefined
        }
      >
        <Select
          loading={sheetDataRequest.loading}
          showSearch
          optionFilterProp="children"
          disabled={!form.getFieldValue('url') || error}
        >
          {sheetData &&
            sheetData.sheets.map(sheetsData => (
              <Select.Option key={sheetsData.properties.title} value={sheetsData.properties.title}>
                {sheetsData.properties.title}
              </Select.Option>
            ))}
        </Select>
      </Form.Item>
      {type === 'external' && (
        <Form.Item
          name="code"
          rules={[
            {
              required: true,
              message: t('global:required-field')
            }
          ]}
          label={t('integrationsPage:code')}
        >
          <Input disabled={!form.getFieldValue('url') || error} />
        </Form.Item>
      )}
      {type === 'external' && (
        <Form.Item
          name="dataType"
          rules={[
            {
              required: true,
              message: t('global:required-field')
            }
          ]}
          label={t('global:type')}
        >
          <Select disabled={!form.getFieldValue('url') || error} optionFilterProp="children">
            {[ReportDataType.actuals, ReportDataType.budget].map(dataType => (
              <Select.Option key={dataType} value={dataType}>
                {t(`global:${dataType}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {type === 'external' && (
        <Form.Item name="dimensionId" label={t('global:dimension')}>
          <CtxDimensionTreeSelect disabled={!form.getFieldValue('url') || error} />
        </Form.Item>
      )}
      {type !== 'actuals' && (
        <Form.Item name="budgetingScenarioId" label={t('global:scenario')}>
          <CtxScenarioLockSelect />
        </Form.Item>
      )}
    </>
  )
}

export default GoogleSheetForm
